/* ==========================================================================
   Bundle PDP review slider.

   Two jobs, both progressive enhancement over a plain overflow-x scroller:

   * the progress bar under the track — width and offset mirror the native
     scrollbar proportions, and the bar stays hidden unless the track really
     overflows, so a short section never shows a dead rail;
   * drag-to-scroll for mouse users, who get no swipe and no arrows in this
     design. Touch and trackpad scrolling are left to the browser.
   ========================================================================== */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 4;

  function setProgress(viewport, track, thumb) {
    var scrollable = track.scrollWidth - track.clientWidth;
    var overflows = scrollable > 1;

    viewport.dataset.overflow = overflows ? 'true' : 'false';
    if (!overflows || !thumb) return;

    var ratio = track.clientWidth / track.scrollWidth;
    var progress = track.scrollLeft / scrollable;

    // The thumb travels the rail's leftover length, so it lands flush with
    // each end instead of overshooting.
    thumb.style.setProperty('--rv-thumb-w', (ratio * 100).toFixed(3) + '%');
    thumb.style.setProperty('--rv-thumb-x', (progress * (1 - ratio) * 100).toFixed(3) + '%');
  }

  function snapToNearest(track) {
    // Measured live off the rendered boxes rather than reconstructed from
    // offsetLeft: the anchor is wherever a snapped card should sit, which is
    // the scrollport's left edge plus the track's own gutter, and every card
    // is compared against it in the same viewport coordinates.
    var pad = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
    var anchor = track.getBoundingClientRect().left + pad;
    var closest = 0;
    var shortest = Infinity;

    track.querySelectorAll('.bundle-rv__card').forEach(function (card) {
      var delta = card.getBoundingClientRect().left - anchor;
      if (Math.abs(delta) < shortest) {
        shortest = Math.abs(delta);
        closest = delta;
      }
    });

    if (shortest === Infinity || shortest < 1) return;

    // scrollTo clamps on its own, so the first and last cards simply come to
    // rest against their ends instead of overshooting.
    track.scrollTo({ left: track.scrollLeft + closest, behavior: 'smooth' });
  }

  function enableDrag(track) {
    // Coarse pointers already swipe; hijacking them would fight the browser's
    // own momentum and snapping.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var startX = 0;
    var startScroll = 0;
    var pointerId = null;
    var moved = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.button !== 0 || pointerId !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;
    });

    track.addEventListener('pointermove', function (event) {
      if (event.pointerId !== pointerId) return;

      var delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!moved) {
        moved = true;
        track.dataset.dragging = 'true';
        try {
          track.setPointerCapture(pointerId);
        } catch (error) {
          /* capture is a nicety; the move handler still fires without it */
        }
      }

      track.scrollLeft = startScroll - delta;
      event.preventDefault();
    });

    function release(event) {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!moved) return;

      // Snapping is off during the drag so the track can follow the cursor
      // freely. Re-snapping on a property change is not dependable across
      // browsers, so the nearest card is scrolled to explicitly instead.
      delete track.dataset.dragging;
      snapToNearest(track);
    }

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);

    // A drag that ends over a card would otherwise fire that card's click.
    track.addEventListener('click', function (event) {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);
  }

  function start(viewport) {
    var track = viewport.querySelector('[data-rv-track]');
    if (!track) return;

    var thumb = viewport.querySelector('[data-rv-thumb]');
    var queued = false;

    function update() {
      queued = false;
      setProgress(viewport, track, thumb);
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(schedule).observe(track);
    }

    // Late-loading card images change scrollWidth after the first measure.
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', schedule, { once: true });
    });

    enableDrag(track);
    update();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-rv-viewport]').forEach(function (viewport) {
      if (viewport.dataset.rvReady === 'true') return;
      viewport.dataset.rvReady = 'true';
      start(viewport);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
